import React, { useState } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Container from "@/components/ui/Container";
import { ArrowRight, MapPin, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { vacancies } from "@/data/vacancies";
import { usePageTitle } from "@/hooks/usePageTitle";

const VacanciesPage = () => {
  usePageTitle("Вакансии");
  const [searchQuery, setSearchQuery] = useState("");

  // This would typically come from API
  // For demo purposes, using hardcoded data

  // Filter by search query
  const filteredVacancies = vacancies.filter(item =>
    searchQuery === "" ||
    item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 bg-secondary">
          <Container>
            <div className="max-w-3xl mx-auto text-center">
              <div className="tag mb-6 animate-fade-in">Карьера в StackAdvance</div>
              <h1 className="heading text-4xl md:text-5xl font-bold mb-6 animate-fade-in" style={{ animationDelay: "0.2s" }}>
                Открытые <span className="text-gradient">вакансии</span>
              </h1>
              <p className="text-muted-foreground text-lg mb-8 animate-fade-in" style={{ animationDelay: "0.4s" }}>
                Присоединяйтесь к нашей команде профессионалов и развивайтесь вместе с нами
              </p>
              
              {/* Search */}
              <div className="relative max-w-xl mx-auto">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Поиск по вакансиям..."
                  className="w-full px-4 py-3 pl-10 bg-white border border-border rounded-full focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              </div>
            </div>
          </Container>
        </section>

        {/* Vacancies List */}
        <section className="py-20">
          <Container>
            {filteredVacancies.length > 0 ? (
              <div className="space-y-6">
                {filteredVacancies.map((vacancy) => (
                  <div 
                    key={vacancy.id} 
                    className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-border hover:border-primary/20 hover:shadow-md transition-all duration-300 p-6"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                          {vacancy.title}
                        </h3>
                        <div className="flex items-center text-muted-foreground mb-3">
                          <MapPin className="h-4 w-4 mr-1" /> 
                          <span>{vacancy.location}</span>
                          {vacancy.salaryRange && (
                            <span className="ml-4 bg-secondary/50 px-2 py-1 rounded text-sm">{vacancy.salaryRange}</span>
                          )}
                        </div>
                        <p className="text-muted-foreground mb-4">
                          {vacancy.description}
                        </p>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Link 
                          to={`/vacancies/${vacancy.id}`}
                          className="button-primary"
                        >
                          Подробнее
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <h3 className="text-xl font-semibold mb-2">Ничего не найдено</h3>
                <p className="text-muted-foreground mb-6">По вашему запросу не найдено вакансий. Попробуйте изменить параметры поиска.</p>
                <button 
                  onClick={() => setSearchQuery("")}
                  className="button-secondary"
                >
                  Сбросить фильтр
                </button>
              </div>
            )}
          </Container>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-secondary">
          <Container>
            <div className="glass rounded-2xl p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="heading text-3xl font-bold mb-4">
                    Не нашли подходящую вакансию?
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Отправьте нам своё резюме, и мы свяжемся с вами, когда появится подходящая позиция. Мы всегда ищем талантливых специалистов для нашей команды.
                  </p>
                  <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                    <Link to="/contact" className="button-primary">
                      Отправить резюме
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </div>
                </div>
                <div className="relative">
                  <div className="aspect-[4/3] bg-white/5 rounded-2xl overflow-hidden p-6">
                    <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=2070')] bg-cover bg-center opacity-10 rounded-2xl"></div>
                    <div className="relative h-full flex flex-col items-center justify-center text-center space-y-4">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                          <rect width="18" height="18" x="3" y="4" rx="2" ry="2"></rect>
                          <line x1="16" x2="16" y1="2" y2="6"></line>
                          <line x1="8" x2="8" y1="2" y2="6"></line>
                          <line x1="3" x2="21" y1="10" y2="10"></line>
                          <path d="m9 16 2 2 4-4"></path>
                        </svg>
                      </div>
                      <h3 className="text-2xl font-semibold">Открытый набор</h3>
                      <p className="text-muted-foreground">
                        Мы постоянно расширяем нашу команду и ищем специалистов с разным опытом и навыками
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default VacanciesPage;
